import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getRequest, apiSuccessHandler, apiErrorHandler, putRequest, postRequest, setApiResult, download } from '../../core/bffApiModule'
import { AppThunk, RootState } from '../../../store/store';
import * as CONSTANTS from '../../../constants/constants';
import * as formatUtils from '../../../utils/formatUtils';
import * as Model from './mutualAidModel';
import moment, { Moment } from 'moment';
import * as messagesUtils from '../../../utils/messagesUtils';

export interface changeCheckTransfer {
  targetDataRow: Model.MutualAidProjectDetailRow
  value: boolean
}

export interface changeCheckTransferSummary {
  targetDataRow: Model.MutualAidProjectListRow
  value: boolean
}

// 初期設定の日付を計算
const setDate = (num: number) => {
  const nowMoment: Moment | undefined = moment(new Date(), moment.ISO_8601);
  return nowMoment === undefined ? "" : nowMoment.add(num, "month").format("YYYYMM");
}

// 元請勤怠実績データ初期化
const initialState: Model.MutualAidPrimeState = {
  dataList: []
  , projectId: ""
  , projectName: ""
  , branchName: ""
  , detailDataList: []
  , detailSummaryDataList: []
  , detailTotal: 0
  , mutualAidProjectDetailCriteria: {
    attendanceYearMonth: ""
    , submitted: "1"
    , approved: "1"
    , requested: "0"
    , linked: "0"
  }
  , allSelect: false
  , existAvailableRow: false
  , mutualAidProjectListCriteria: {
    branchName: ""
    , projectName: ""
    , firstPartnerCompanyName: ""
    , firstPartnerCompanyNameKana: ""
    , fromYYYYMM: setDate(-1)
    , toYYYYMM: setDate(0)
    , submitted: "1"
    , approved: "1"
    , requested: "0"
    , linked: "0"
    , sortItem: ""
    , sortOrder: ""
    , offset: 0
    , limit: 10
  }
  , listTotal: 0
  , checkedMutualAidStatusCount: {
    submitted: 0
    , approved: 0
    , requested: 0
    , linked: 0
  }
  , requestModalDisagreeVisibleState: false
  , serviceTermsCheck: "0"
  , resultList: []
  , resultListTotal: 0
  , mutualAidResultListCriteria: {
    sortItem: ""
    , sortOrder: ""
    , offset: 0
    , limit: 10
  }
  , resultDetailList: []
  , resultDetailTotal: 0
  , resultDetailAllConstructionCodeList: []
  , mutualAidResultDetailCriteria: {
    attendanceYearMonth: ""
    , linkedDatetime: ""
    , sortItem: ""
    , sortOrder: ""
    , offset: 0
    , limit: 10
  }
  , errorReportModalVisibleState: false
  , resultErrorReportList: []
  , resultErrorReportTotal: 0
  , resultDownloadModalVisibleState: false
  , errorReportAttachModalVisibleState: false
  , mutualAidErrorReportModalCriteria: {
    sortItem: ""
    , sortOrder: ""
    , offset: 0
    , limit: 10
  }
  , errorReportAttachModalResultList: []
  , errorReportAttachModalMessage: ""
};

export const mutualAidSlice = createSlice({
  name: 'mutualAidPrime',
  initialState,
  reducers: {
    // -------------
    // プロジェクト一覧
    // -------------

    prepareMoveToDetail: (state, action: PayloadAction<Model.MutualAidProjectListRow>) => {
      state.projectId = action.payload.projectId;
      state.mutualAidProjectDetailCriteria.attendanceYearMonth = formatUtils.formatMonthNonSlash(action.payload.attendanceYearMonth);
      state.mutualAidProjectDetailCriteria.submitted = String(action.payload.submitted);
      state.mutualAidProjectDetailCriteria.approved = String(action.payload.approved);
      state.mutualAidProjectDetailCriteria.requested = String(action.payload.requested);
      state.mutualAidProjectDetailCriteria.linked = String(action.payload.linked);
    },

    prepareMoveToDetailCriteria: (state, action: PayloadAction<Model.MutualAidProjectListCriteria>) => {

      let criteria: Model.MutualAidProjectDetailCriteria = {
        attendanceYearMonth: state.mutualAidProjectDetailCriteria.attendanceYearMonth,
        submitted: action.payload.submitted,
        approved: action.payload.approved,
        requested: action.payload.requested,
        linked: action.payload.linked,
      }

      state.mutualAidProjectDetailCriteria = criteria;
    },

    /**
     * 建退共連携結果 一覧画面へ遷移する前に検索条件を初期化する
     * @param state 
     * @param action 
     */
    prepareMoveToResultListCriteria: (state, action: PayloadAction<Model.MutualAidResultListCriteria>) => {
      state.mutualAidResultListCriteria.sortItem = "";
      state.mutualAidResultListCriteria.sortOrder = "";
      state.mutualAidResultListCriteria.offset = 0;
      state.mutualAidResultListCriteria.limit = 10;
    },

    /**
     * 建退共連携結果 明細画面へ遷移する前に検索条件を初期化する
     * 全工事コードも初期化する
     * @param state 
     * @param action 
     */
    prepareMoveToResultDetailCriteria: (state, action: PayloadAction<Model.MutualAidResultListRow>) => {
      state.mutualAidResultDetailCriteria.linkedDatetime = action.payload.linkedDatetime;
      state.mutualAidResultDetailCriteria.sortItem = "";
      state.mutualAidResultDetailCriteria.sortOrder = "";
      state.mutualAidResultDetailCriteria.offset = 0;
      state.mutualAidResultDetailCriteria.limit = 10;
      state.resultDetailAllConstructionCodeList = [];
    },

    /**
     * 建退共連携結果 明細画面のエラー一覧ダイアログのCriteriaを初期化
     * @param state 
     * @param action 
     */
    prepareMoveToErrorReportModalCriteria: (state, action: PayloadAction<Model.MutualAidResultDetailCriteria>) => {
      state.mutualAidErrorReportModalCriteria.linkedDatetime = action.payload.linkedDatetime;
      state.mutualAidErrorReportModalCriteria.sortItem = "";
      state.mutualAidErrorReportModalCriteria.sortOrder = "";
      state.mutualAidErrorReportModalCriteria.offset = 0;
      state.mutualAidErrorReportModalCriteria.limit = 10;
    },

    initDataList: (state) => {
      state.dataList = initialState.dataList;
    },
    // Use the PayloadAction type to declare the contents of `action.payload`
    setDataListOfMutualAidProjectList: (state, action: PayloadAction<Model.MutualAidProjectListApiData>) => {

      state.dataList = initialState.dataList;

      const responseMutualAidProjectRecordList: Model.MutualAidProjectListRow[] = action.payload.mutualAidProjectRecordList || [];
      let mutualAidProjectRecordList: Array<Model.MutualAidProjectListRow> = [];
      responseMutualAidProjectRecordList.forEach((mutualAidProjectRecord: Model.MutualAidProjectListRow) => {

        const rec: Model.MutualAidProjectListRow = {
          key: mutualAidProjectRecord.projectId + mutualAidProjectRecord.attendanceYearMonth
          , projectId: mutualAidProjectRecord.projectId
          , projectName: mutualAidProjectRecord.projectName
          , branchName: mutualAidProjectRecord.branchName
          , firstPartnerCompanyList: mutualAidProjectRecord.firstPartnerCompanyList
          , attendanceYearMonth: mutualAidProjectRecord.attendanceYearMonth
          , constructWorkType: mutualAidProjectRecord.constructWorkType
          , submitted: mutualAidProjectRecord.submitted
          , approved: mutualAidProjectRecord.approved
          , requested: mutualAidProjectRecord.requested
          , linked: mutualAidProjectRecord.linked
        };
        mutualAidProjectRecordList.push(rec);
      });

      state.dataList = mutualAidProjectRecordList;
      state.listTotal = action.payload.total;
    },

    /**
     * 検索条件のstate更新
     */
    setListCriteria: (state, action: PayloadAction<Model.MutualAidProjectListCriteria>) => {
      let criteria: Model.MutualAidProjectListCriteria = {
        branchName: action.payload.branchName,
        projectName: action.payload.projectName,
        firstPartnerCompanyName: action.payload.firstPartnerCompanyName,
        firstPartnerCompanyNameKana: action.payload.firstPartnerCompanyNameKana,
        fromYYYYMM: action.payload.fromYYYYMM,
        toYYYYMM: action.payload.toYYYYMM,
        submitted: action.payload.submitted,
        approved: action.payload.approved,
        requested: action.payload.requested,
        linked: action.payload.linked,
        sortItem: action.payload.sortItem,
        sortOrder: action.payload.sortOrder,
        offset: action.payload.offset,
        limit: action.payload.limit
      }
      return Object.assign({}, state, { mutualAidProjectListCriteria: criteria })
    },

    /**
     * 各行のチェックボックス変更
     */
    onChangeRowSelectSummary: (state, action: PayloadAction<changeCheckTransferSummary>) => {
      let userWorkStatusList: Array<Model.MutualAidProjectListRow> = [];
      let existOnFlag = false;

      state.dataList.forEach((summaryData: Model.MutualAidProjectListRow) => {
        const rec: Model.MutualAidProjectListRow = {
          key: summaryData.key
          , projectId: summaryData.projectId
          , projectName: summaryData.projectName
          , branchName: summaryData.branchName
          , firstPartnerCompanyList: summaryData.firstPartnerCompanyList
          , attendanceYearMonth: summaryData.attendanceYearMonth
          , constructWorkType: summaryData.constructWorkType
          , submitted: summaryData.submitted
          , approved: summaryData.approved
          , requested: summaryData.requested
          , linked: summaryData.linked
        };

        userWorkStatusList.push(rec);
      });

      return Object.assign({}, state, { dataList: userWorkStatusList }, { existCheckedSummary: existOnFlag });
    },

    /**
     * 全選択チェックボックス変更
     */
    onChangeAllSelectSummary: (state, action: PayloadAction<boolean>) => {
      let userWorkStatusList: Array<Model.MutualAidProjectListRow> = [];

      let checkDataExist = false;
      // 一覧のチェック状態を全て変更
      state.dataList.forEach((summaryData: Model.MutualAidProjectListRow) => {
        const rec: Model.MutualAidProjectListRow = {
          key: summaryData.key
          , projectId: summaryData.projectId
          , projectName: summaryData.projectName
          , branchName: summaryData.branchName
          , firstPartnerCompanyList: summaryData.firstPartnerCompanyList
          , attendanceYearMonth: summaryData.attendanceYearMonth
          , constructWorkType: summaryData.constructWorkType
          , submitted: summaryData.submitted
          , approved: summaryData.approved
          , requested: summaryData.requested
          , linked: summaryData.linked
        };

        userWorkStatusList.push(rec);
        checkDataExist = action.payload;
      });

      return Object.assign({}, state, { dataList: userWorkStatusList }, { existCheckedSummary: checkDataExist });
    },

    // -------------
    // プロジェクト詳細
    // -------------

    /**
     * 検索条件の初期化
     */
    initCriteria: (state) => {
      let mutualAidProjectDetailCriteria = {
        attendanceYearMonth: setDate(0)
        , submitted: "1"
        , approved: "1"
        , requested: "0"
        , linked: "0"
      }

      let mutualAidProjectListCriteria = {
        branchName: ""
        , projectName: ""
        , firstPartnerCompanyName: ""
        , firstPartnerCompanyNameKana: ""
        , fromYYYYMM: setDate(-1)
        , toYYYYMM: setDate(0)
        , submitted: "1"
        , approved: "1"
        , requested: "0"
        , linked: "0"
        , sortItem: ""
        , sortOrder: ""
        , offset: 0
        , limit: 10
      }

      return Object.assign({}, state, { mutualAidProjectDetailCriteria: mutualAidProjectDetailCriteria }, { mutualAidProjectListCriteria: mutualAidProjectListCriteria });
    },

    /**
     * 検索条件のstate更新
     */
    setCriteria: (state, action: PayloadAction<Model.MutualAidProjectDetailCriteria>) => {
      let criteria: Model.MutualAidProjectDetailCriteria = {
        attendanceYearMonth: action.payload.attendanceYearMonth,
        submitted: action.payload.submitted,
        approved: action.payload.approved,
        requested: action.payload.requested,
        linked: action.payload.linked
      }
      return Object.assign({}, state, { mutualAidProjectDetailCriteria: criteria })
    },

    /**
     * 一覧の初期化
     */
    initDetailDataList: (state) => {
      return Object.assign({}, state, { detailDataList: initialState.detailDataList })
    },

    /**
     * 一覧レコードのセット
     */
    setDetailDataListOfMutualAidProjectPrime: (state, action: PayloadAction<Model.getMutualAidProjectDetailDataList.Response>) => {
      let userWorkStatusList: Array<Model.MutualAidProjectDetailRow> = [];
      let total = action.payload.total;
      let branchName = action.payload.branchName;
      let projectId = action.payload.projectId;
      let projectName = action.payload.projectName;
      let submitted = action.payload.submitted;
      let approved = action.payload.approved;
      let requested = action.payload.requested;
      let linked = action.payload.linked;
      let existAvailableRow: boolean = false
      let detailSummaryDataList: Array<Model.MutualAidProjectDetailSummaryRow> = [];

      action.payload.userWorkStatusList.forEach((userWorkStatus: Model.getMutualAidProjectDetailDataList.UserWorkStatusList) => {
        userWorkStatus.projectWorkStatusList.forEach((projectWorkStatus: Model.getMutualAidProjectDetailDataList.ProjectWorkStatusList) => {

          const rec: Model.MutualAidProjectDetailRow = {
            key: projectWorkStatus.workRecordSubmissionHistoryId
            , workRecordSubmissionHistoryId: projectWorkStatus.workRecordSubmissionHistoryId
            , selectFlag: false
            , approvalStatus: projectWorkStatus.approvalStatus
            , offerFlag: projectWorkStatus.offerFlag
            , belongCompanyId: projectWorkStatus.belongCompanyId
            , belongCompanyName: projectWorkStatus.belongCompanyName
            , workerId: userWorkStatus.workerId
            , lastNameKana: userWorkStatus.lastNameKana
            , firstNameKana: userWorkStatus.firstNameKana
            , firstPartnerCompanyId: projectWorkStatus.firstPartnerCompanyId
            , updateDatetime: projectWorkStatus.updateDatetime
            , mutualAidInsuredNo: projectWorkStatus.mutualAidInsuredNo
            , isEnableAttendanceProcess: projectWorkStatus.isEnableAttendanceProcess
          };

          userWorkStatusList.push(rec);

          if (rec.isEnableAttendanceProcess) {
            // 処理可能な行があるので、全選択チェックボックスを表示する
            existAvailableRow = true;
          }
        });
      });


      let checkedMutualAidStatusCount: Model.CheckedMutualAidStatusCount = {
        submitted: 0,
        approved: 0,
        requested: 0,
        linked: 0,
      }

      let detailSummaryDataRow: Model.MutualAidProjectDetailSummaryRow = {
        key: "",
        total: total,
        submitted: submitted,
        approved: approved,
        requested: requested,
        linked: linked
      }
      detailSummaryDataList.push(detailSummaryDataRow);

      return Object.assign(
        {}, state,
        { detailTotal: total },
        { detailDataList: userWorkStatusList },
        { detailSummaryDataList: detailSummaryDataList },
        { allSelect: false },
        { existChecked: false },
        { branchName: branchName },
        { projectId: projectId },
        { projectName: projectName },
        { existAvailableRow: existAvailableRow },
        { checkedMutualAidStatusCount: checkedMutualAidStatusCount },
        { submitted: submitted },
        { approved: approved },
        { requested: requested },
        { linked: linked },
      )
    },

    /**
     * 全選択チェックボックス変更
     */
    onChangeAllSelect: (state, e: any) => {
      let userWorkStatusList: Array<Model.MutualAidProjectDetailRow> = [];

      // 一覧のチェック状態を全て変更
      state.detailDataList.forEach((detailData: Model.MutualAidProjectDetailRow) => {
        const rec: Model.MutualAidProjectDetailRow = {
          key: detailData.workRecordSubmissionHistoryId
          , workRecordSubmissionHistoryId: detailData.workRecordSubmissionHistoryId
          , selectFlag: detailData.isEnableAttendanceProcess ? e.payload.target.checked : false
          , approvalStatus: detailData.approvalStatus
          , offerFlag: detailData.offerFlag
          , belongCompanyId: detailData.belongCompanyId
          , belongCompanyName: detailData.belongCompanyName
          , workerId: detailData.workerId
          , lastNameKana: detailData.lastNameKana
          , firstNameKana: detailData.firstNameKana
          , firstPartnerCompanyId: detailData.firstPartnerCompanyId
          , updateDatetime: detailData.updateDatetime
          , mutualAidInsuredNo: detailData.mutualAidInsuredNo
          , isEnableAttendanceProcess: detailData.isEnableAttendanceProcess
        };

        userWorkStatusList.push(rec);
      });

      let checkedMutualAidStatusCount: Model.CheckedMutualAidStatusCount = getSelectedMutualAidStatusCount(userWorkStatusList);

      return Object.assign(
        {}, state,
        { allSelect: e.payload.target.checked },
        { detailDataList: userWorkStatusList },
        { checkedMutualAidStatusCount: checkedMutualAidStatusCount },
      );
    },

    /**
     * 各行のチェックボックス変更
     */
    onChangeRowSelect: (state, action: PayloadAction<changeCheckTransfer>) => {
      let userWorkStatusList: Array<Model.MutualAidProjectDetailRow> = [];
      let existOffFlag = false;

      state.detailDataList.forEach((detailData: Model.MutualAidProjectDetailRow) => {
        const rec: Model.MutualAidProjectDetailRow = {
          key: detailData.workRecordSubmissionHistoryId
          , workRecordSubmissionHistoryId: detailData.workRecordSubmissionHistoryId
          , selectFlag: action.payload.targetDataRow.key === detailData.key ? action.payload.value : detailData.selectFlag
          , approvalStatus: detailData.approvalStatus
          , offerFlag: detailData.offerFlag
          , belongCompanyId: detailData.belongCompanyId
          , belongCompanyName: detailData.belongCompanyName
          , workerId: detailData.workerId
          , lastNameKana: detailData.lastNameKana
          , firstNameKana: detailData.firstNameKana
          , firstPartnerCompanyId: detailData.firstPartnerCompanyId
          , updateDatetime: detailData.updateDatetime
          , mutualAidInsuredNo: detailData.mutualAidInsuredNo
          , isEnableAttendanceProcess: detailData.isEnableAttendanceProcess
        };

        userWorkStatusList.push(rec);

        if (rec.isEnableAttendanceProcess) {
          if (!rec.selectFlag) {
            // 処理可能行　かつ　 選択されていない
            existOffFlag = true;
          }
        }
      });

      let allSelect = existOffFlag ? false : true;
      let checkedMutualAidStatusCount: Model.CheckedMutualAidStatusCount = getSelectedMutualAidStatusCount(userWorkStatusList);

      return Object.assign(
        {}, state,
        { allSelect: allSelect },
        { detailDataList: userWorkStatusList },
        { checkedMutualAidStatusCount: checkedMutualAidStatusCount },
      );
    },
    
    /**
     * データ連携同意モーダルの表示切替
     */
    reflectRequestModalDisagreeVisibleState: (state, action: PayloadAction<boolean>) => {
      // モーダル表示切替
      state.requestModalDisagreeVisibleState = action.payload
    },
    
    /**
     * 約款同意チェックボックスのstate更新
     */
    setTermOfServiceCheck: (state, action: PayloadAction<string>) => {
      state.serviceTermsCheck = action.payload;
    },

    /**
     * 建退共連携結果一覧を反映
     * @param state 
     * @param action 
     */
    setDataListOfMutualAidResultList: (state, action: PayloadAction<Model.MutualAidResultListApiData>) => {

      state.dataList = initialState.dataList;

      const responseMutualAidResultRecordList: Model.MutualAidResultListRow[] = action.payload.mutualAidResultRecordList || [];
      let mutualAidResultRecordList: Array<Model.MutualAidResultListRow> = [];
      responseMutualAidResultRecordList.forEach((mutualAidResultRecord: Model.MutualAidResultListRow) => {

        const rec: Model.MutualAidResultListRow = {
          key: mutualAidResultRecord.linkedDatetime
          , linkedDatetime: mutualAidResultRecord.linkedDatetime
        };
        mutualAidResultRecordList.push(rec);
      });

      state.resultList = mutualAidResultRecordList;
      state.resultListTotal = action.payload.total;
    },

    /**
     * 建退共連携結果明細を反映
     * 総件数も反映
     * 初回は全工事コードも反映
     * @param state 
     * @param action 
     */
    setDataListOfMutualAidResultDetailList: (state, action: PayloadAction<Model.MutualAidResultDetailApiData>) => {

      const responseMutualAidResultRecordList: Model.MutualAidResultDetailRow[] = action.payload.mutualAidResultDetailRecordList || [];
      let mutualAidResultDetailRecordList: Array<Model.MutualAidResultDetailRow> = [];
      responseMutualAidResultRecordList.forEach((mutualAidResultRecord: Model.MutualAidResultDetailRow) => {

        const rec: Model.MutualAidResultDetailRow = {
          key: mutualAidResultRecord.projectId
          , primeCompanyBranchCode: mutualAidResultRecord.primeCompanyBranchCode
          , branchBussinessCode: mutualAidResultRecord.branchBussinessCode
          , constructionCode: mutualAidResultRecord.constructionCode
          , projectId: mutualAidResultRecord.projectId
          , projectName: mutualAidResultRecord.projectName
          , projectBranchId: mutualAidResultRecord.projectBranchId
          , projectBranchName: mutualAidResultRecord.projectBranchName
          , status: mutualAidResultRecord.status
        };
        mutualAidResultDetailRecordList.push(rec);
      });

      state.resultDetailList = mutualAidResultDetailRecordList;
      state.resultDetailTotal = action.payload.total;
      if (action.payload.allConstructionCodeList && action.payload.allConstructionCodeList.length > 0) {
        state.resultDetailAllConstructionCodeList = action.payload.allConstructionCodeList;
      }
    },

    /**
     * 連携結果一覧画面　検索条件のstate更新
     */
    setResultListCriteria: (state, action: PayloadAction<Model.MutualAidResultListCriteria>) => {
      let criteria: Model.MutualAidResultListCriteria = {
        sortItem: action.payload.sortItem,
        sortOrder: action.payload.sortOrder,
        offset: action.payload.offset,
        limit: action.payload.limit
      }
      return Object.assign({}, state, { mutualAidResultListCriteria: criteria })
    },

    /**
     * 連携結果明細画面　検索条件のstate更新
     */
    setResultDetailCriteria: (state, action: PayloadAction<Model.MutualAidResultDetailCriteria>) => {
      let criteria: Model.MutualAidResultDetailCriteria = {
        attendanceYearMonth: action.payload.attendanceYearMonth,
        linkedDatetime: action.payload.linkedDatetime,
        sortItem: action.payload.sortItem,
        sortOrder: action.payload.sortOrder,
        offset: action.payload.offset,
        limit: action.payload.limit
      }
      return Object.assign({}, state, { mutualAidResultDetailCriteria: criteria })
    },

    /**
     * 検索条件のstate更新
     */
    setErrorReportModalCriteria: (state, action: PayloadAction<Model.MutualAidErrorReportModalCriteria>) => {
      let criteria: Model.MutualAidErrorReportModalCriteria = {
        sortItem: action.payload.sortItem,
        sortOrder: action.payload.sortOrder,
        offset: action.payload.offset,
        limit: action.payload.limit
      }
      return Object.assign({}, state, { mutualAidErrorReportModalCriteria: criteria })
    },

    /**
     * モーダルの表示/非表示切り替え
     */
    reflectErrorReportAttachModalState: (state, action: PayloadAction<boolean>) => {
      return Object.assign({}, state, { errorReportAttachModalVisibleState: action.payload });
    },

    /**
     * モーダルの表示/非表示切り替え
     */
    reflectErrorReportModalState: (state, action: PayloadAction<boolean>) => {
      return Object.assign({}, state, { errorReportModalVisibleState: action.payload });
    },

    /**
     * エラーレポートの取得結果を画面に反映
     * @param state 
     * @param action 
     */
    setDataListOfMutualAidErrorReportList: (state, action: PayloadAction<Model.MutualAidResultErrorReportApiData>) => {

      const responseRecordList: Model.MutualAidResultErrorReportRow[] = action.payload.mutualAidResultErrorRecordList || [];
      let recordList: Array<Model.MutualAidResultErrorReportRow> = [];
      responseRecordList.forEach((mutualAidResultRecord: Model.MutualAidResultErrorReportRow) => {

        const rec: Model.MutualAidResultErrorReportRow = {
          key: mutualAidResultRecord.projectId
          , projectId: mutualAidResultRecord.projectId
          , projectName: mutualAidResultRecord.projectName
          , infoType: mutualAidResultRecord.infoType
          , message: mutualAidResultRecord.message
          , errorValue: mutualAidResultRecord.errorValue
          , belongCompanyId: mutualAidResultRecord.belongCompanyId
          , belongCompanyName: mutualAidResultRecord.belongCompanyName
          , workerId: mutualAidResultRecord.workerId
          , workerName: mutualAidResultRecord.workerName
          , firstPartnerCompanyIds: mutualAidResultRecord.firstPartnerCompanyIds
          , firstPartnerCompanyNames: mutualAidResultRecord.firstPartnerCompanyNames
        };
        recordList.push(rec);
      });

      state.resultErrorReportList = recordList;
      state.resultErrorReportTotal = action.payload.total;
    },

    /**
     * モーダルの表示/非表示切り替え
     */
    reflectResultDownloadModalState: (state, action: PayloadAction<boolean>) => {
      return Object.assign({}, state, {   resultDownloadModalVisibleState: action.payload });
    },

    /**
     * エラーレポートファイルの処理結果を画面に反映
     * @param state 
     * @param action 
     */
    setErrorReportAttachModalResultList: (state, action: PayloadAction<Array<any>>) => {
      state.errorReportAttachModalResultList = action.payload;
    }, 

    /**
     * エラーレポートファイルのチェック結果を画面に反映
     */
    setErrorReportAttachModalMessage: (state, action: PayloadAction<string>) => {
      state.errorReportAttachModalMessage = action.payload;
    },
  },
});

export const {
  prepareMoveToDetail
  , prepareMoveToDetailCriteria
  , prepareMoveToResultListCriteria
  , prepareMoveToResultDetailCriteria
  , prepareMoveToErrorReportModalCriteria
  , initDataList
  , setDataListOfMutualAidProjectList
  , setListCriteria
  , initCriteria
  , setCriteria
  , initDetailDataList
  , setDetailDataListOfMutualAidProjectPrime
  , onChangeAllSelect
  , onChangeAllSelectSummary
  , onChangeRowSelect
  , onChangeRowSelectSummary
  , reflectRequestModalDisagreeVisibleState
  , setTermOfServiceCheck
  , setDataListOfMutualAidResultList
  , setDataListOfMutualAidResultDetailList
  , setResultListCriteria
  , setResultDetailCriteria
  , setErrorReportModalCriteria
  , reflectErrorReportAttachModalState
  , reflectErrorReportModalState
  , setDataListOfMutualAidErrorReportList
  , reflectResultDownloadModalState
  , setErrorReportAttachModalResultList
  , setErrorReportAttachModalMessage
} = mutualAidSlice.actions;

/**
 * 状態毎のチェックされた勤怠件数を取得
 * @param rows 
 * @returns 
 */
function getSelectedMutualAidStatusCount(rows: Model.MutualAidProjectDetailRow[]): Model.CheckedMutualAidStatusCount {
  let selectedMutualAidStatusCount: Model.CheckedMutualAidStatusCount = {
    submitted: 0,
    approved: 0,
    requested: 0,
    linked: 0,
  }

  for (const r of rows) {
    if (r.selectFlag) {
      if (r.approvalStatus != CONSTANTS.ATTENDANCE_STATUS_APPROVED) {
        selectedMutualAidStatusCount.submitted += 1;
      } else {
        switch (r.offerFlag) {
          case CONSTANTS.OFFER_FLAG_APPROVED:
            selectedMutualAidStatusCount.approved += 1;
            break;
          case CONSTANTS.OFFER_FLAG_REQUESTED:
            selectedMutualAidStatusCount.requested += 1;
            break;
          case CONSTANTS.OFFER_FLAG_LINKED:
            selectedMutualAidStatusCount.linked += 1;
            break;
        }
      }
    }
  }

  return selectedMutualAidStatusCount;
}

/**
 * 建退共連携プロジェクト一覧取得_元請API呼び出し
 * @param params 
 * @returns 
 */
export const getDataListAsync = (params: Model.getMutualAidProjectListDataList.Request): AppThunk => async dispatch => {

  try {
    // GET-APIを呼び出します。
    const response = await getRequest(CONSTANTS.URL_MUTUAL_AID_PRIME_LIST, {
      params: params
    });
    // 結果をstateに設定します。
    dispatch(setDataListOfMutualAidProjectList(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

/**
 * 建退共連携プロジェクト詳細取得_元請API呼び出し
 * @param params 
 */
export const getDetailDataListAsync = (params: Model.getMutualAidProjectDetailDataList.Request): AppThunk => async dispatch => {
  try {
    // GET-APIを呼び出します。
    const response = await getRequest(CONSTANTS.URL_MUTUAL_AID_PRIME_DETAIL, {
      params: params
    });
    // 結果をstateに設定します。
    dispatch(setDetailDataListOfMutualAidProjectPrime(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
}

/**
 * 建退共約款同意状況取得API呼び出し
 * @param params 
 */
export const getMutualAidTermsAgreeAsync = (params: Model.getMutualAidTermsAgree.Request): AppThunk<Promise<string>> => {
  // APIのレスポンスをすぐ判定に使うためそのままreturnしている（ステートを挟んだ場合即時反映できない）
  return async dispatch =>  {
    try {
      // GET-APIを呼び出します。
      const response = await getRequest(CONSTANTS.URL_MUTUAL_AID_PRIME_TERMS_AGREE, {params});
      // 共通成功時処理を呼び出します。
      dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
      return response.data.data.agreementStateType;
    } catch (error: any) {
      // 共通エラー時処理を呼び出します。
      dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
    }
  }
}

/**
 * 建退共約款同意状況更新API呼び出し
 * @param params 
 */
export const putMutualAidTermsAgreeAsync = (params: Model.putMutualAidTermsAgree.Request): AppThunk => async dispatch => {
  try {
    // PUT-APIを呼び出します。
    const response = await putRequest(CONSTANTS.URL_MUTUAL_AID_PRIME_TERMS_AGREE, params);
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_PUT));
  } catch (error: any) {
    // 共通エラー時処理を呼び出します。
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_PUT));
  }
}

/**
 * 建退共連携ステータス変更API呼び出し
 * @param putData 
 */
export const putMutualAidStatusAsync = (putData: Model.putStatus.Request): AppThunk => async dispatch => {
  try {
    // PUT-APIを呼び出します。
    const response = await putRequest(CONSTANTS.URL_MUTUAL_AID_PRIME_STATUS, putData);

    // APIが返却したエラーコードがいずれかの場合、セッションストレージに保管されている処理成功メッセージを書き換える
    // ・認可チェック失敗
    // ・更新処理0件
    let message = "";
    let processName = "";
    let map = new Map();
    map.set("1", "元請会社");
    map.set("2", "協力会社");
    map.set("3", "協力会社");
    map.set("4", "被共済者");
    map.set("5", "プロジェクト");
    switch (response.data.cmn.errorCode) {
      case "EA5000001":
          processName = getAeendanceProcessType(putData.processType);
          message = messagesUtils.getMessage(response.data.cmn.errorCode, [processName]);
          messagesUtils.setMessageInSession(message, CONSTANTS.MESSAGE_TYPE_ERROR);
          break;
      case "EA5000004":
          message = messagesUtils.getMessage(response.data.cmn.errorCode);
          messagesUtils.setMessageInSession(message, CONSTANTS.MESSAGE_TYPE_ERROR);
          break;
      default:
        if (response.data.data.errorList.length > 0) {
          if (response.data.data.count > 0) {
            message += response.data.data.count + "件申請しました。";
          } else {
            message += "申請ができませんでした。";
          }
          message += "以下のエラーメッセージをご確認ください。"
          for (let error of response.data.data.errorList) {
            let base = "\n・【" + map.get(error.errorType) + "】" + error.errorTargetName + (error.errorType == "4" && error.errorTargetName.length > 1 ? "さん" : "");
            let base2 = "";
            if (error.errorType == "1" || error.errorType == "5") {
              base2 = "グリーンサイトから修正してください。"
            } else if (error.errorType == "2" || error.errorType == "3") {
              base2 = "グリーンサイトから修正するよう、" + (error.errorCode == "E0201" ? "該当の協力会社" : error.errorTargetName) + "の担当者へ依頼してください。";
            } else if (error.errorType == "4") {
              base2 = "グリーンサイトから修正するよう、所属会社の担当者へ依頼してください。";
            }
            switch (error.errorCode) {
              case "E0101":
                message += base + "の共済契約者番号が取得できませんでした。" + base2;
                break;
              case "E0102":
                message += base + "の共済契約者番号の桁数が7桁ではありません。" + base2;
                break;
              case "E0103":
                message += base + "の共済契約者番号に使用できない文字が含まれています。" + base2;
                break;
              case "E0104":
                message += base + "の共済契約者番号および法人番号が取得できませんでした。" + base2;
                break;
              case "E0105":
                message += base + "の法人番号の桁数が13桁ではありません。" + base2;
                break;
              case "E0106":
                message += base + "の法人番号に使用できない文字が含まれています。" + base2;
                break;
              case "E0201":
                message += base + "会社名が取得できませんでした。" + base2;
                break;
              case "E0202":
                message += base + "の会社名の文字数が50文字を超えています。" + base2;
                break;
              case "E0203":
                message += base + "の会社名に使用できない文字が含まれています。" + base2;
                break;
              case "E0301":
                message += base + "の郵便番号が取得できませんでした。" + base2;
                break;
              case "E0302":
                message += base + "の郵便番号の形式に誤りがあります。" + base2;
                break;
              case "E0303":
                message += base + "の郵便番号に使用できない文字が含まれています。" + base2;
                break;
              case "E0401":
                message += base + "の住所（都道府県）が取得できませんでした。" + base2;
                break;
              case "E0501":
                message += base + "の住所（市区町村）が取得できませんでした。" + base2;
                break;
              case "E0502":
                message += base + "の住所（市区町村）の文字数が50文字を超えています。" + base2;
                break;
              case "E0503":
                message += base + "の住所（市区町村）に使用できない文字が含まれています。" + base2;
                break;
              case "E0601":
                message += base + "の住所（丁目・番地）が取得できませんでした。" + base2;
                break;
              case "E0602":
                message += base + "の住所（丁目・番地）の文字数が50文字を超えています。" + base2;
                break;
              case "E0603":
                message += base + "の住所（丁目・番地）に使用できない文字が含まれています。" + base2;
                break;
              case "E0702":
                message += base + "の住所（建物名等）の文字数が50文字を超えています。" + base2;
                break;
              case "E0703":
                message += base + "の住所（建物名等）に使用できない文字が含まれています。" + base2;
                break;
              case "E0802":
                message += base + "の電話番号の形式が誤っています。" + base2;
                break;
              case "E0901":
                message += base + "は、「外部プロダクト向けデータ連携サービス」に未同意のため送信対象から除外されました。送信を行うには、対象の協力会社様にて事前の同意手続きを行っていただく必要があります。";
                break;
              case "E2101":
                message += base + (error.errorTargetName.length > 1 ? "の" : "") + "氏名（セイ）が取得できませんでした。" + base2;
                break;
              case "E2102":
                message += base + "の氏名（セイ）の文字数が50文字を超えています。" + base2;
                break;
              case "E2103":
                message += base + "の氏名（セイ）に使用できない文字が含まれています。" + base2;
                break;
              case "E2201":
                message += base + (error.errorTargetName.length > 1 ? "の" : "") + "氏名（メイ）が取得できませんでした。" + base2;
                break;
              case "E2202":
                message += base + "の氏名（メイ）の文字数が50文字を超えています。" + base2;
                break;
              case "E2203":
                message += base + "の氏名（メイ）に使用できない文字が含まれています。" + base2;
                break;
              case "E2301":
                message += base + "の被共済者番号が取得できませんでした。" + base2;
                break;
              case "E2302":
                message += base + "の被共済者番号の桁数が9桁または10桁ではありません。" + base2;
                break;
              case "E2303":
                message += base + "の被共済者番号に使用できない文字が含まれています。" + base2;
                break;
              case "E2401":
                message += base + "は、個人情報の外部提供に同意していないため、今回の送信対象から除外されました。";
                break;
              case "E4101":
                message += base + "プロジェクト名が取得できませんでした。" + base2;
                break;
              case "E4102":
                message += base + "のプロジェクト名の文字数が50文字を超えています。" + base2;
                break;
              case "E4103":
                message += base + "のプロジェクト名に使用できない文字が含まれています。" + base2;
                break;
              case "E4201":
                message += base + "の工事区分が取得できませんでした。" + base2;
                break;
            }
          }
        }
        if (message.length > 0) messagesUtils.setMessageInSession(message, CONSTANTS.MESSAGE_TYPE_ERROR);
    }
    // 共通成功時処理を呼び出します。
    dispatch(setApiResult({ status: response.status, errorCode: "", requestMethod: CONSTANTS.REQUEST_METHOD_PUT, url: response.config.url }));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_PUT));
  }
}

/**
 * 実行メッセージ取得
 * @param processType 
 */
const getAeendanceProcessType = (processType: string): string => {
  // 処理タイプ毎にメッセージを取得
  let args = "";
  switch (processType) {
    case CONSTANTS.ATTENDANCE_PROCESS_TYPE.APPROVAL:
      args = "承認";
      break;
    case CONSTANTS.ATTENDANCE_PROCESS_TYPE.CONFIRM:
      args = "確認";
      break;
    case CONSTANTS.ATTENDANCE_PROCESS_TYPE.REMAND:
      args = "差戻";
      break;
    case CONSTANTS.ATTENDANCE_PROCESS_TYPE.APPROVAL_CANCEL:
      args = "承認取消";
      break;
    case CONSTANTS.ATTENDANCE_PROCESS_TYPE.FORCE_APPROVAL:
      args = "外部承認";
      break;
  }
  return args;
}

/**
 * 建退共連携結果一覧取得_元請API呼び出し
 * @param params 
 * @returns 
 */
export const getResultListAsync = (params: Model.getMutualAidResultListDataList.Request): AppThunk => async dispatch => {

  try {
    // GET-APIを呼び出します。
    const response = await getRequest(CONSTANTS.URL_MUTUAL_AID_RESULT, {
      params: params
    });
    // 結果をstateに設定します。
    dispatch(setDataListOfMutualAidResultList(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

//　連携結果ダウンロード
export const downloadResultRecord = (request: Model.downloadResultRecord.Request): AppThunk => async (dispatch) => {
  try {
    // GET-APIを呼び出します。
    // ダウンロードします
    const response = await download(CONSTANTS.URL_MUTUAL_AID_RESULT_DOWNLOAD, 
      request,
      {responseType: "blob"}
    );
    // // 共通成功時処理を呼び出します。
    if (response.status === CONSTANTS.API_STATUS_200) {
      dispatch(setApiResult({ status: response.status, errorCode: "", requestMethod: CONSTANTS.REQUEST_METHOD_GET, url: response.config.url }));

      // ダイアログを閉じる
      dispatch(reflectResultDownloadModalState(false));
    }
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

/**
 * 建退共連携結果明細_元請API呼び出し
 * @param params 
 * @returns 
 */
export const getResultDetailAsync = (params: Model.getMutualAidResultDetailDataList.Request): AppThunk => async dispatch => {

  try {
    // GET-APIを呼び出します。
    const response = await getRequest(CONSTANTS.URL_MUTUAL_AID_RESULT_DETAIL, {
      params: params
    });
    // 結果をstateに設定します。
    dispatch(setDataListOfMutualAidResultDetailList(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

/**
 * エラーレポート添付
 * @param putData 
 * @returns 
 */
export const putErrorReportAttachAsync = (putData: Model.putErrorReportAttach.Request): AppThunk => async dispatch => {

  try {
    // PUT-APIを呼び出します。
    const response = await putRequest(CONSTANTS.URL_MUTUAL_AID_ERROR_REPORT_ATTACH, putData);
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_PUT));
    dispatch(setErrorReportAttachModalResultList(response.data.data.resultList));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_PUT));
  }
};

/**
 * エラーレポート添付状態変更
 * @param putData 
 * @returns 
 */
export const putErrorReportStatusAsync = (putData: Model.putErrorReportStatus.Request): AppThunk => async dispatch => {

  try {
    // PUT-APIを呼び出します。
    const response = await putRequest(CONSTANTS.URL_MUTUAL_AID_ERROR_REPORT, putData);
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_PUT));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    console.log(error);
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_PUT));
  }
};

/**
 * 建退共連携結果明細_エラー一覧表示
 * @param params 
 * @returns 
 */
export const getErrorReport = (params: Model.getErrorReport.Request): AppThunk => async dispatch => {

  try {
    // GET-APIを呼び出します。
    const response = await getRequest(CONSTANTS.URL_MUTUAL_AID_RESULT_ERROR_GET, {
      params: params
    });
    // 結果をstateに設定します。
    dispatch(setDataListOfMutualAidErrorReportList(response.data.data));
    // 共通成功時処理を呼び出します。
    dispatch(apiSuccessHandler(response, CONSTANTS.REQUEST_METHOD_GET));
  } catch (error :any) {
    // 共通エラー時処理を呼び出します。
    dispatch(apiErrorHandler(error.response, CONSTANTS.REQUEST_METHOD_GET));
  }
};

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const storedDataList = (state: RootState) => state.mutualAidPrime.dataList;
export const storedDetailDataList = (state: RootState) => state.mutualAidPrime.detailDataList;
export const storedDetailSummaryDataList = (state: RootState) => state.mutualAidPrime.detailSummaryDataList;
export const storedAllSelect = (state: RootState) => state.mutualAidPrime.allSelect;
export const storedDetailCriteria = (state: RootState) => state.mutualAidPrime.mutualAidProjectDetailCriteria;
export const storedDetailTotal = (state: RootState) => state.mutualAidPrime.detailTotal;
export const storedBranchName = (state: RootState) => state.mutualAidPrime.branchName;
export const storedProjectId = (state: RootState) => state.mutualAidPrime.projectId;
export const storedProjectName = (state: RootState) => state.mutualAidPrime.projectName;
export const storedExsitAvailabletRow = (state: RootState) => state.mutualAidPrime.existAvailableRow;
export const storedListCriteria = (state: RootState) => state.mutualAidPrime.mutualAidProjectListCriteria;
export const storedListTotal = (state: RootState) => state.mutualAidPrime.listTotal;
export const storedCheckedMutualAidStatusCount = (state: RootState) => state.mutualAidPrime.checkedMutualAidStatusCount;
export const storedRequestModalDisagreeVisibleState = (state: RootState) => state.mutualAidPrime.requestModalDisagreeVisibleState;
export const serviceTermsCheckState = (state: RootState) => state.mutualAidPrime.serviceTermsCheck
export const storedResultDataList = (state: RootState) => state.mutualAidPrime.resultList;
export const storedResultDataListTotal = (state: RootState) => state.mutualAidPrime.resultListTotal;
export const storedResultListCriteria = (state: RootState) => state.mutualAidPrime.mutualAidResultListCriteria;
export const storedResultDetailList = (state: RootState) => state.mutualAidPrime.resultDetailList;
export const storedResultDetailTotal = (state: RootState) => state.mutualAidPrime.resultDetailTotal;
export const storedResultDetailAllConstructionCodeList = (state: RootState) => state.mutualAidPrime.resultDetailAllConstructionCodeList;
export const storedResultDetailCriteria = (state: RootState) => state.mutualAidPrime.mutualAidResultDetailCriteria;
export const storedErrorReportModalVisibleState = (state: RootState) => state.mutualAidPrime.errorReportModalVisibleState;
export const storedResultErrorReportList = (state: RootState) => state.mutualAidPrime.resultErrorReportList;
export const storedErrorReportModalTotal = (state: RootState) => state.mutualAidPrime.resultErrorReportTotal;
export const storedResultDownloadModalVisibleState = (state: RootState) => state.mutualAidPrime.resultDownloadModalVisibleState;
export const storedErrorReportAttachModalVisibleState = (state: RootState) => state.mutualAidPrime.errorReportAttachModalVisibleState;
export const storedErrorReportModalCriteria = (state: RootState) => state.mutualAidPrime.mutualAidErrorReportModalCriteria;
export const storedErrorReportAttachModalResultList = (state: RootState) => state.mutualAidPrime.errorReportAttachModalResultList;
export const storedErrorReportAttachModalMessage = (state: RootState) => state.mutualAidPrime.errorReportAttachModalMessage;

export default mutualAidSlice.reducer;
